import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Edit2, Search } from 'lucide-react';
import { groupNotesApi } from '../../api/groupNotes';
import { NOTE_TYPE_CONFIG, type GroupNote, type NoteType } from '../../types/groupNote';
import { Card, EmptyState } from '../ui';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { EditGroupNoteSheet } from './EditGroupNoteSheet';

interface GroupNotesListProps {
  groupId: string;
  onAddNote: () => void;
}

export const GroupNotesList: React.FC<GroupNotesListProps> = ({
  groupId,
  onAddNote,
}) => {
  const [selectedType, setSelectedType] = useState<NoteType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNote, setEditingNote] = useState<GroupNote | null>(null);
  const [hasEverHadNotes, setHasEverHadNotes] = useState(false);
  const queryClient = useQueryClient();
  const filterContainerRef = useRef<HTMLDivElement>(null);
  const activeFilterRef = useRef<HTMLButtonElement>(null);

  // Fetch notes
  const { data, isLoading } = useQuery({
    queryKey: ['group-notes', groupId, selectedType],
    queryFn: () =>
      groupNotesApi.getByGroupId(groupId, {
        limit: 50,
        type: selectedType === 'all' ? undefined : selectedType,
      }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => groupNotesApi.delete(groupId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-notes', groupId] });
    },
  });

  const handleDelete = (noteId: string, title: string) => {
    if (window.confirm(`Удалить заметку "${title}"?`)) {
      deleteMutation.mutate(noteId);
    }
  };

  // Prepare data before any conditional returns
  const allNotes = data?.notes || [];

  // Запоминаем что у нас были записи (чтобы продолжать показывать фильтры)
  useEffect(() => {
    if (allNotes.length > 0 && !hasEverHadNotes) {
      setHasEverHadNotes(true);
    }
  }, [allNotes.length, hasEverHadNotes]);

  // Filter notes by search query
  const notes = useMemo(() => {
    if (!searchQuery.trim()) return allNotes;

    const query = searchQuery.toLowerCase();
    return allNotes.filter(
      (note) =>
        note.title.toLowerCase().includes(query) ||
        note.content.toLowerCase().includes(query)
    );
  }, [allNotes, searchQuery]);

  // Auto-scroll to selected filter when it changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activeFilterRef.current && filterContainerRef.current) {
        activeFilterRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedType]);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-24 rounded-xl"
            style={{ backgroundColor: 'var(--bg-secondary)' }}
          />
        ))}
      </div>
    );
  }

  // Показываем поиск и фильтры если есть записи (включая случай когда по фильтру ничего не найдено)
  const showFiltersAndSearch = hasEverHadNotes || allNotes.length > 0;

  return (
    <>
      <div className="space-y-4">
        {/* Search */}
        {showFiltersAndSearch && (
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--text-hint)' }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по записям..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl text-[15px]"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: 'none',
              }}
            />
          </div>
        )}

        {/* Filter by type */}
        {showFiltersAndSearch && (
          <div ref={filterContainerRef} className="flex gap-2 overflow-x-auto pb-2">
            <FilterChip
              ref={selectedType === 'all' ? activeFilterRef : null}
              label="Все"
              active={selectedType === 'all'}
              onClick={() => setSelectedType('all')}
            />
            {Object.entries(NOTE_TYPE_CONFIG).map(([type, config]) => (
              <FilterChip
                key={type}
                ref={selectedType === type ? activeFilterRef : null}
                label={`${config.icon} ${config.label}`}
                active={selectedType === type}
                onClick={() => setSelectedType(type as NoteType)}
              />
            ))}
          </div>
        )}

        {/* Add note button */}
        {allNotes.length > 0 && (
          <button
            onClick={onAddNote}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors"
            style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF',
            }}
          >
            <Plus size={18} />
            Добавить запись
          </button>
        )}

        {/* Empty state */}
        {allNotes.length === 0 ? (
          <EmptyState
            icon={Plus}
            title="Нет записей"
            description="Добавьте первую заметку о группе"
            primaryAction={{
              label: 'Добавить заметку',
              onClick: onAddNote,
            }}
          />
        ) : notes.length === 0 ? (
          <EmptyState
            icon={Search}
            title="Ничего не найдено"
            description={
              searchQuery
                ? `По запросу "${searchQuery}" записей не найдено`
                : `Нет записей типа "${NOTE_TYPE_CONFIG[selectedType as NoteType]?.label || selectedType}"`
            }
          />
        ) : (
          /* Notes list */
          <div className="space-y-3">
            {notes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                onEdit={() => setEditingNote(note)}
                onDelete={() => handleDelete(note.id, note.title)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit sheet */}
      {editingNote && (
        <EditGroupNoteSheet
          groupId={groupId}
          note={editingNote}
          onClose={() => setEditingNote(null)}
        />
      )}
    </>
  );
};

// Filter chip component
const FilterChip = React.forwardRef<
  HTMLButtonElement,
  {
    label: string;
    active: boolean;
    onClick: () => void;
  }
>(({ label, active, onClick }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    className="px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors"
    style={{
      backgroundColor: active ? 'var(--accent)' : 'var(--bg-secondary)',
      color: active ? '#FFFFFF' : 'var(--text-primary)',
    }}
  >
    {label}
  </button>
));

FilterChip.displayName = 'FilterChip';

// Note card component
const NoteCard: React.FC<{
  note: GroupNote;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ note, onEdit, onDelete }) => {
  const config = NOTE_TYPE_CONFIG[note.note_type];
  const relativeTime = formatDistanceToNow(new Date(note.created_at), {
    addSuffix: true,
    locale: ru,
  });

  return (
    <Card className="relative">
      <div className="flex gap-3">
        {/* Icon */}
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
          style={{ backgroundColor: `${config.color}18` }}
        >
          {config.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3
                className="text-[15px] font-semibold truncate"
                style={{ color: 'var(--text-primary)' }}
              >
                {note.title}
              </h3>
              <p
                className="text-[13px] mt-1 line-clamp-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                {note.content}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-1 flex-shrink-0">
              <button
                onClick={onEdit}
                className="p-2 rounded-lg transition-colors"
                style={{ color: 'var(--accent)' }}
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={onDelete}
                className="p-2 rounded-lg transition-colors"
                style={{ color: '#EF4444' }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          {/* Meta */}
          <div className="flex items-center gap-2 mt-2">
            <span
              className="text-[11px] px-2 py-0.5 rounded"
              style={{
                backgroundColor: `${config.color}18`,
                color: config.color,
              }}
            >
              {config.label}
            </span>
            <span
              className="text-[11px]"
              style={{ color: 'var(--text-hint)' }}
            >
              {relativeTime}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
};
